import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Alert, Loader, Badge, Table, Dialog } from '@dreamtree-org/twreact-ui';
import { RefreshCw, Trash2, ScanSearch, DownloadCloud } from 'lucide-react';
import Can from '@rbac/Can';
import { useGitApp, errText } from './useGitApp';

const STATUS_VARIANT = { queued: 'default', running: 'primary', succeeded: 'success', failed: 'error' };
const IN_FLIGHT = ['queued', 'running'];
const POLL_MS = 4000;

const pretty = (raw) => {
  if (!raw) return null;
  try {
    return JSON.stringify(typeof raw === 'string' ? JSON.parse(raw) : raw, null, 2);
  } catch {
    return String(raw);
  }
};

const queueNotice = (res, ok) => {
  if (res?.enqueued) return ok;
  const why = res?.error ? ` (${res.error})` : '';
  return `Recorded, but the job queue is unreachable${why} — is Redis running?`;
};

/**
 * Background runs for this app: repo ingest and gap scans.
 *
 * Polls only while something is actually in flight — an idle feed shouldn't hit the API
 * forever (same reasoning as the scraper list).
 */
export default function ActivityFeed({ appId }) {
  const { act } = useGitApp(appId);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [confirmClear, setConfirmClear] = useState(false);
  const [detail, setDetail] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await act('activity');
      setRows(d.activity || []);
      setErr('');
    } catch (ex) {
      setErr(errText(ex, 'Could not load activity'));
    } finally {
      setLoading(false);
    }
  }, [act]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const active = rows.some((r) => IN_FLIGHT.includes(r.status));
    clearTimeout(timer.current);
    if (active) timer.current = setTimeout(load, POLL_MS);
    return () => clearTimeout(timer.current);
  }, [rows, load]);

  const run = async (kind) => {
    setBusy(kind);
    setErr('');
    try {
      const r = await act('run', { kind });
      setNotice(queueNotice(r, `${kind} queued.`));
      await load();
    } catch (ex) {
      setErr(errText(ex, `Could not queue ${kind}`));
    } finally {
      setBusy('');
    }
  };

  const clearFinished = async () => {
    setBusy('clear');
    try {
      const r = await act('clearActivity', { all: true });
      setNotice(`${r.deleted} finished run(s) cleared.`);
      setConfirmClear(false);
      await load();
    } catch (ex) {
      setErr(errText(ex, 'Could not clear activity'));
    } finally {
      setBusy('');
    }
  };

  const columns = [
    { key: 'kind', label: 'Kind', render: (r) => <span className="font-mono text-xs">{r.kind}</span> },
    { key: 'title', label: 'Title' },
    { key: 'status', label: 'Status', render: (r) => <Badge variant={STATUS_VARIANT[r.status] || 'default'}>{r.status}</Badge> },
    { key: 'attempts', label: 'Tries' },
    { key: 'created_at', label: 'Queued' },
    {
      key: 'detail',
      label: '',
      render: (r) => (
        (r.result || r.error)
          ? <button type="button" className="text-primary-600 text-xs hover:underline" onClick={() => setDetail(r)}>Details</button>
          : null
      ),
    },
  ];

  if (loading) return <Loader text="Loading activity…" />;

  return (
    <div className="space-y-4">
      <Card className="p-3 flex items-center gap-2 flex-wrap">
        <Can module="development" action="update">
          <Button size="sm" leftIcon={<ScanSearch size={14} />} loading={busy === 'gap_scan'} onClick={() => run('gap_scan')}>
            Run gap scan
          </Button>
          <Button size="sm" variant="secondary" leftIcon={<DownloadCloud size={14} />} loading={busy === 'ingest_repo'} onClick={() => run('ingest_repo')}>
            Re-ingest
          </Button>
        </Can>
        <button type="button" className="opacity-50 hover:opacity-100 ml-auto" onClick={load} title="Refresh">
          <RefreshCw size={15} />
        </button>
        {rows.some((r) => !IN_FLIGHT.includes(r.status)) ? (
          <Can module="development" action="update">
            <button type="button" className="flex items-center gap-1 text-xs text-error-600 hover:underline" onClick={() => setConfirmClear(true)}>
              <Trash2 size={13} /> Clear finished
            </button>
          </Can>
        ) : null}
      </Card>

      {err ? <Alert variant="error" title="Activity">{err}</Alert> : null}
      {notice ? <Alert variant="success" title="Activity">{notice}</Alert> : null}

      {rows.length === 0 ? (
        <Card className="p-6 text-sm opacity-60">
          No background runs yet. A gap scan clones the repo and files findings as tickets.
          It needs the development worker running (DEV_RUNNER_ENABLED=1).
        </Card>
      ) : (
        <Table data={rows} columns={columns} pagination withAction={false} />
      )}

      {detail ? (
        <Dialog isOpen onClose={() => setDetail(null)} title={`${detail.kind} · ${detail.status}`} size="lg">
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            {detail.error ? (
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider opacity-60 mb-1">Error</h3>
                <pre className="text-[11px] font-mono bg-error-50 text-error-800 rounded p-3 overflow-auto whitespace-pre-wrap">{detail.error}</pre>
              </div>
            ) : null}
            {detail.result ? (
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider opacity-60 mb-1">Result</h3>
                <pre className="text-[11px] font-mono bg-secondary-50 dark:bg-secondary-800 rounded p-3 overflow-auto whitespace-pre">{pretty(detail.result)}</pre>
              </div>
            ) : null}
          </div>
        </Dialog>
      ) : null}

      {confirmClear ? (
        <Dialog isOpen onClose={() => setConfirmClear(false)} title="Clear finished runs?">
          <div className="space-y-4">
            <p className="text-sm">
              Removes the records of completed and failed runs for this app. Queued and running
              jobs are left alone, and any tickets a scan already filed are kept.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirmClear(false)}>Cancel</Button>
              <Button variant="destructive" onClick={clearFinished} loading={busy === 'clear'}>Clear</Button>
            </div>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
